import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { formatUsuarioLabel } from '@leadsphere/types';

import { Badge, type BadgeTone } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { CheckboxDropdown } from '../../components/ui/CheckboxDropdown.js';
import { ConfirmModal } from '../../components/ui/ConfirmModal.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { RowMenu } from '../../components/ui/RowMenu.js';
import { Table, type TableColumn } from '../../components/ui/Table.js';
import { ToastStack } from '../../components/ui/Toast.js';
import { useApi } from '../../hooks/useApi.js';
import { useCanClient } from '../../hooks/useCanClient.js';
import { useToasts } from '../../hooks/useToasts.js';
import { ApiError } from '../../lib/api.js';
import { ordenarCatalogo } from '../../lib/catalogos.js';
import { PlataformaLogo } from './PlataformaLogo.js';
import type { CatalogoItem, ClienteListItem, ListarClientesResponse, Usuario } from './types.js';

const ESTATUS_TONE: Record<string, BadgeTone> = {
  activo: 'success',
  pausado: 'orange',
  pendiente: 'info',
  fuera_del_servicio: 'neutral',
};

const ESTATUS_LABEL: Record<string, string> = {
  activo: 'Activo',
  pausado: 'Pausado',
  pendiente: 'Pendiente',
  fuera_del_servicio: 'Fuera del servicio',
};

const ESTATUS_OPCIONES = ['activo', 'pausado', 'pendiente', 'fuera_del_servicio'] as const;

// Prompt BK-04 — mismo criterio que ClienteDetalle.tsx.
const PRIORIDAD_TONE: Record<string, BadgeTone> = { A: 'danger', B: 'orange', C: 'neutral' };
const PROPUESTA_KEYS = ['doc_plan_trabajo', 'doc_carta_intencion', 'doc_propuesta', 'doc_contrato', 'doc_sla', 'doc_nda'] as const;

const LIMIT = 25;

// Moisés (14-oct): por defecto no se ven los fuera del servicio; quien los
// quiera ver los marca en el filtro. Vacío = todos los estatus.
const ESTATUS_POR_DEFECTO = ['activo', 'pausado', 'pendiente'];
const ESTATUS_FILTRO = [
  { value: 'activo', label: 'Activo' },
  { value: 'pausado', label: 'Pausado' },
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'fuera_del_servicio', label: 'Fuera del servicio' },
];

export function ClientesPage() {
  const navigate = useNavigate();
  const { get, patch } = useApi();
  const queryClient = useQueryClient();
  const { canEditClient, canChangeStatus, puedeReactivarFueraDeServicio, canCopyEmails } = useCanClient();
  const { toasts, showToast, dismiss } = useToasts();

  const [search, setSearch] = useState('');
  const [estatus, setEstatus] = useState<string[]>(ESTATUS_POR_DEFECTO);
  const [level, setLevel] = useState('');
  const [kamId, setKamId] = useState('');
  const [page, setPage] = useState(1);
  const [estatusPendiente, setEstatusPendiente] = useState<{ id: string; nuevo: string } | null>(null);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    if (estatus.length) p.set('estatus', estatus.join(','));
    if (level) p.set('level', level);
    if (kamId) p.set('kam_id', kamId);
    p.set('page', String(page));
    p.set('limit', String(LIMIT));
    return p.toString();
  }, [search, estatus, level, kamId, page]);

  const { data, isLoading } = useQuery({
    queryKey: ['clientes', params],
    queryFn: () => get<ListarClientesResponse>(`/clientes?${params}`),
  });

  const { data: usuarios } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => get<Usuario[]>('/usuarios'),
  });
  const kams = useMemo(() => (usuarios ?? []).filter(u => u.rol === 'kam' && u.activo), [usuarios]);

  // Antes hardcodeado como ['T1','T2','T3'] — por eso los tiers nuevos no
  // aparecían en este filtro ni con F5. Mismo prefijo ['catalogo','tier'] que
  // consume/invalida NuevoCliente.tsx y CatalogoCard.tsx.
  const { data: tierOpts } = useQuery({
    queryKey: ['catalogo', 'tier'],
    queryFn: () => get<CatalogoItem[]>('/catalogos/tier'),
  });
  const tierOrdenado = useMemo(() => ordenarCatalogo(tierOpts ?? [], 'tier'), [tierOpts]);

  const clientes = data?.data ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.meta.total / data.meta.limit)) : 1;
  // Como conjunto: desmarcar y volver a marcar un estatus cambia el orden del
  // arreglo, no el filtro.
  const estatusCambiado = [...estatus].sort().join(',') !== [...ESTATUS_POR_DEFECTO].sort().join(',');
  const hasFilters = Boolean(search || estatusCambiado || level || kamId);

  const resetFiltros = () => {
    setSearch('');
    setEstatus(ESTATUS_POR_DEFECTO);
    setLevel('');
    setKamId('');
    setPage(1);
  };

  const cambiarEstatusMutation = useMutation({
    mutationFn: ({ id, nuevo }: { id: string; nuevo: string }) => patch(`/clientes/${id}/estatus`, { estatus: nuevo }),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      queryClient.invalidateQueries({ queryKey: ['cliente', id] });
      showToast('success', 'Estatus actualizado correctamente.');
    },
    onError: (err: unknown) => {
      showToast('error', err instanceof ApiError ? err.message : 'No fue posible cambiar el estatus.');
    },
  });

  function handleEstatusChange(cliente: ClienteListItem, nuevo: string) {
    if (nuevo === cliente.estatus) return;
    if (nuevo === 'fuera_del_servicio') {
      setEstatusPendiente({ id: cliente.id, nuevo });
      return;
    }
    cambiarEstatusMutation.mutate({ id: cliente.id, nuevo });
  }

  function confirmarEstatusPendiente() {
    if (estatusPendiente) cambiarEstatusMutation.mutate(estatusPendiente);
    setEstatusPendiente(null);
  }

  // Prompt BJ-12 — botón "Copiar correos" (head/root): endpoint dedicado, no
  // los datos ya cargados en `clientes` — ClientesPage solo trae
  // plataformas_resumen por fila, nunca contactos (eso vive únicamente en
  // ClienteDetalleResponse, un cliente a la vez).
  const copiarCorreosMutation = useMutation({
    mutationFn: () => get<{ correos: string[] }>('/clientes/correos-activos'),
    onSuccess: async ({ correos }) => {
      await navigator.clipboard.writeText(correos.join(','));
      showToast('success', `${correos.length} correo(s) copiados al portapapeles.`);
    },
    onError: (err: unknown) => {
      showToast('error', err instanceof ApiError ? err.message : 'No fue posible copiar los correos.');
    },
  });

  const columns: TableColumn<ClienteListItem>[] = [
    {
      key: 'cliente',
      header: 'Cliente',
      render: c => (
        <div>
          <div style={{ fontWeight: 700, color: 'var(--heading)' }}>{c.nombre_comercial}</div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{c.razon_social}</div>
        </div>
      ),
    },
    { key: 'tier', header: 'Tier', render: c => c.level ?? '—' },
    {
      key: 'estatus',
      header: 'Estatus',
      render: c => <Badge tone={ESTATUS_TONE[c.estatus] ?? 'neutral'}>{ESTATUS_LABEL[c.estatus] ?? c.estatus}</Badge>,
    },
    {
      key: 'prioridad',
      header: 'Prioridad',
      render: c => (c.prioridad ? <Badge tone={PRIORIDAD_TONE[c.prioridad] ?? 'neutral'}>{c.prioridad}</Badge> : '—'),
    },
    { key: 'kam', header: 'KAM(s) Asignado(s)', render: c => (c.kams.length ? c.kams.map(k => k.nombre).join(', ') : '—') },
    {
      key: 'propuesta',
      header: 'Propuesta',
      render: c => `${PROPUESTA_KEYS.filter(k => c[k]).length}/${PROPUESTA_KEYS.length}`,
    },
    {
      key: 'plataformas',
      header: 'Plataformas',
      render: c => (
        <div style={{ display: 'flex' }}>
          {c.plataformas_resumen.map((p, i) => (
            <PlataformaLogo key={i} nombre={p} />
          ))}
        </div>
      ),
    },
    {
      key: 'acciones',
      header: '',
      render: c => {
        const esFueraDeServicio = c.estatus === 'fuera_del_servicio';
        const items = [
          ...(canEditClient
            ? [
                {
                  label: 'Editar',
                  onClick: () => navigate(`/clientes/${c.id}/editar`),
                },
              ]
            : []),
          ...(canChangeStatus
            ? ESTATUS_OPCIONES.filter(opcion => opcion !== c.estatus).map(opcion => ({
                label: `Cambiar a ${ESTATUS_LABEL[opcion]}`,
                disabled: esFueraDeServicio && !puedeReactivarFueraDeServicio,
                title:
                  esFueraDeServicio && !puedeReactivarFueraDeServicio
                    ? 'Cliente fuera de servicio — para reactivar, solicite la reactivación al administrador global'
                    : undefined,
                onClick: () => handleEstatusChange(c, opcion),
              }))
            : []),
        ];
        return (
          <div onClick={e => e.stopPropagation()}>
            <RowMenu items={items} />
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--heading)' }}>Clientes</div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3 }}>
            {data ? `Mostrando ${clientes.length} de ${data.meta.total}` : 'Cargando…'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {canCopyEmails && (
            <Button
              variant="secondary"
              onClick={() => copiarCorreosMutation.mutate()}
              disabled={copiarCorreosMutation.isPending}
            >
              Copiar correos
            </Button>
          )}
          <Button onClick={() => navigate('/clientes/nuevo')}>Nuevo Cliente</Button>
        </div>
      </div>

      <div className="filters-bar">
        <input
          className="filter-input filter-search"
          type="text"
          placeholder="Buscar cliente..."
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <div style={{ minWidth: 230 }}>
          <CheckboxDropdown
            options={ESTATUS_FILTRO}
            selected={estatus}
            onChange={valores => {
              setEstatus(valores);
              setPage(1);
            }}
            placeholder="Todos los estatus"
          />
        </div>
        <select
          className="filter-input"
          value={level}
          onChange={e => {
            setLevel(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Tier</option>
          {tierOrdenado.map(o => (
            <option key={o.id} value={o.valor}>
              {o.valor}
            </option>
          ))}
        </select>
        <select
          className="filter-input"
          value={kamId}
          onChange={e => {
            setKamId(e.target.value);
            setPage(1);
          }}
        >
          <option value="">KAM</option>
          {kams.map(u => (
            <option key={u.id} value={u.id}>
              {/* Prompt BJ-04 — formatUsuarioLabel(): puesto con asterisco si es_interno. */}
              {u.puesto ? `${u.nombre} (${formatUsuarioLabel(u)})` : u.nombre}
            </option>
          ))}
        </select>
        {hasFilters && (
          <button className="filter-reset" onClick={resetFiltros}>
            ✕ Limpiar
          </button>
        )}
      </div>

      <Table
        columns={columns}
        data={clientes}
        rowKey={c => c.id}
        onRowClick={c => navigate(`/clientes/${c.id}`)}
        emptyMessage={isLoading ? 'Cargando…' : 'No hay clientes que coincidan con los filtros'}
        footer={
          <>
            <span className="table-count">{data ? `Mostrando ${clientes.length} de ${data.meta.total}` : '—'}</span>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        }
      />

      <ConfirmModal
        open={estatusPendiente !== null}
        title="¿Marcar cliente fuera del servicio?"
        body="Esta acción es irreversible desde esta interfaz: una vez marcado como 'Fuera del servicio', el cliente no podrá reactivarse aquí — para volver a operar con él solicite la reactivación al administrador global."
        confirmLabel="Sí, marcar fuera del servicio"
        danger
        onConfirm={confirmarEstatusPendiente}
        onCancel={() => setEstatusPendiente(null)}
      />

      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}
